import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Trash2, Edit2, Save, Users, ArrowLeft, Mail, Download, Eye, PieChart, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');
const today = () => new Date().toISOString().split('T')[0];
const MONTH_LABELS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const newDraft = () => ({
  id: `draft-${Date.now()}-${Math.random()}`,
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  rib: '',
  mise: 0,
  devise: 'EUR',
  pourcentage: 0,
  date_entree: today(),
  date_sortie: '',
  statut: 'actif',
});

export default function InvestisseursPanel({ onBack, hideBackButton = false }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState([newDraft()]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const now = new Date();
  // Par défaut = mois écoulé
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [sendMonth, setSendMonth] = useState(defaultMonth);
  const [sendYear, setSendYear] = useState(defaultYear);
  const [sending, setSending] = useState(false);
  const [runningScheduler, setRunningScheduler] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null); // { name, month, year }

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/investisseurs`);
      setItems(res.data?.items || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement investisseurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalDraftMise =
    draft.reduce((s, r) => s + (parseFloat(r.mise) || 0), 0);
  const totalSavedMise = items.reduce((s, i) => s + (parseFloat(i.mise) || 0), 0);
  const projectedTotal = totalSavedMise + totalDraftMise;
  const computePct = (mise, base) => {
    const m = parseFloat(mise) || 0;
    return base > 0 ? (m / base) * 100 : 0;
  };

  const addDraftRow = () => setDraft((a) => [...a, newDraft()]);
  const removeDraftRow = (id) => setDraft((a) => a.filter((r) => r.id !== id));
  const updateDraftRow = (id, field, value) =>
    setDraft((a) => a.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const sendMonthlyReleves = async () => {
    if (!window.confirm(`Envoyer le relevé PDF de ${sendMonth}/${sendYear} à tous les investisseurs par email ?`)) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/investisseurs/releves/send-monthly`,
        { month: sendMonth, year: sendYear }
      );
      const { sent_count = 0, failed_count = 0, failed = [] } = res.data || {};
      if (failed_count === 0) {
        toast.success(`Relevés envoyés à ${sent_count} investisseur(s)`);
      } else {
        toast.warning(`${sent_count} envoyé(s), ${failed_count} échec(s)`);
        console.warn('Envois échoués:', failed);
      }
      loadLogs();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi des relevés');
    } finally {
      setSending(false);
    }
  };

  const runScheduler = async () => {
    if (!window.confirm('Simuler l\'envoi automatique du 1er du mois (relevés du mois écoulé) ?')) return;
    setRunningScheduler(true);
    try {
      const res = await axios.post(`${API}/api/investisseurs/releves/run-scheduler`);
      const { sent_count = 0, failed_count = 0, period_label } = res.data || {};
      if (failed_count === 0) {
        toast.success(`Envoi auto ${period_label} : ${sent_count} envoyé(s)`);
      } else {
        toast.warning(`Envoi auto ${period_label} : ${sent_count} OK · ${failed_count} échec(s)`);
      }
      loadLogs();
      setShowLogs(true);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erreur lors du déclenchement');
    } finally {
      setRunningScheduler(false);
    }
  };

  const loadLogs = async () => {
    try {
      const res = await axios.get(`${API}/api/investisseurs/releves/logs`);
      setLogs(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const downloadInvestorReleve = async (inv) => {
    try {
      const res = await axios.get(
        `${API}/api/investisseurs/releve?month=${sendMonth}&year=${sendYear}&inv_id=${inv.id}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Releve_${inv.prenom || ''}_${inv.nom || ''}_${sendYear}_${String(sendMonth).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('Erreur téléchargement relevé');
    }
  };

  const previewInvestorReleve = async (inv) => {
    try {
      const res = await axios.get(
        `${API}/api/investisseurs/releve?month=${sendMonth}&year=${sendYear}&inv_id=${inv.id}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewMeta({
        name: `${inv.prenom || ''} ${inv.nom || ''}`.trim() || 'Investisseur',
        month: sendMonth,
        year: sendYear,
        inv,
      });
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'aperçu");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewMeta(null);
  };

  const saveDraft = async () => {
    const valid = draft.filter((r) => r.nom && parseFloat(r.mise) > 0);
    if (valid.length === 0) {
      toast.error('Aucun investisseur valide (nom + mise requis)');
      return;
    }
    try {
      for (const row of valid) {
        await axios.post(`${API}/api/investisseurs`, {
          nom: row.nom,
          prenom: row.prenom,
          email: row.email,
          telephone: row.telephone,
          rib: row.rib,
          mise: parseFloat(row.mise) || 0,
          devise: row.devise || 'EUR',
          pourcentage: computePct(row.mise, projectedTotal),
          date_entree: row.date_entree,
          date_sortie: row.statut === 'sorti' ? row.date_sortie : null,
          statut: row.statut,
        });
      }
      toast.success(`${valid.length} investisseur(s) enregistré(s)`);
      setDraft([newDraft()]);
      load();
    } catch (e) {
      console.error(e);
      toast.error("Erreur à l'enregistrement");
    }
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API}/api/investisseurs/${editing.id}`, {
        nom: editing.nom,
        prenom: editing.prenom,
        email: editing.email,
        telephone: editing.telephone,
        rib: editing.rib,
        mise: parseFloat(editing.mise) || 0,
        devise: editing.devise || 'EUR',
        pourcentage: computePct(editing.mise, totalSavedMise),
        date_entree: editing.date_entree,
        date_sortie: editing.statut === 'sorti' ? editing.date_sortie : null,
        statut: editing.statut,
      });
      toast.success('Investisseur modifié');
      setEditing(null);
      load();
    } catch (e) {
      console.error(e);
      toast.error('Erreur à la modification');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/investisseurs/${deleteTarget.id}`);
      toast.success('Investisseur supprimé');
      setDeleteTarget(null);
      load();
    } catch (e) {
      console.error(e);
      toast.error('Erreur à la suppression');
    }
  };

  const renderDraftRow = (row) => (
    <tr key={`draft-${row.id}`} className="border-b bg-indigo-50/30 hover:bg-indigo-50">
      <td className="px-2 py-1">
        <Input
          value={row.nom}
          onChange={(e) => updateDraftRow(row.id, 'nom', e.target.value)}
          placeholder="Nom *"
          className="h-8 border-indigo-200"
        />
      </td>
      <td className="px-2 py-1">
        <Input
          value={row.prenom}
          onChange={(e) => updateDraftRow(row.id, 'prenom', e.target.value)}
          placeholder="Prénom"
          className="h-8 border-indigo-200"
        />
      </td>
      <td className="px-2 py-1">
        <Input
          type="email"
          value={row.email}
          onChange={(e) => updateDraftRow(row.id, 'email', e.target.value)}
          placeholder="email@..."
          className="h-8 border-indigo-200"
        />
      </td>
      <td className="px-2 py-1">
        <Input
          type="tel"
          value={row.telephone}
          onChange={(e) => updateDraftRow(row.id, 'telephone', e.target.value)}
          placeholder="+33..."
          className="h-8 border-indigo-200"
        />
      </td>
      <td className="px-2 py-1">
        <Input
          value={row.rib}
          onChange={(e) => updateDraftRow(row.id, 'rib', e.target.value)}
          placeholder="FR76 ..."
          className="h-8 border-indigo-200 font-mono text-xs"
        />
      </td>
      <td className="px-2 py-1">
        <div className="flex gap-1">
          <Input
            type="number"
            value={row.mise}
            onChange={(e) => updateDraftRow(row.id, 'mise', parseFloat(e.target.value) || 0)}
            placeholder="Mise *"
            className="h-8 border-indigo-200 text-right font-semibold text-indigo-700 flex-1"
            min="0"
            step="0.01"
            data-testid={`draft-mise-${row.id}`}
          />
          <select
            value={row.devise || 'EUR'}
            onChange={(e) => updateDraftRow(row.id, 'devise', e.target.value)}
            className="h-8 border border-indigo-200 rounded text-xs px-1 bg-white"
            data-testid={`draft-devise-${row.id}`}
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="CNY">CNY</option>
            <option value="XAF">FCFA</option>
          </select>
        </div>
      </td>
      <td className="px-2 py-1">
        <div
          className="h-8 flex items-center justify-end px-2 text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded"
          title="Calculé automatiquement : mise / capital total"
          data-testid={`draft-pct-${row.id}`}
        >
          {computePct(row.mise, projectedTotal).toFixed(2)} %
        </div>
      </td>
      <td className="px-2 py-1">
        <Input
          type="date"
          value={row.date_entree}
          onChange={(e) => updateDraftRow(row.id, 'date_entree', e.target.value)}
          className="h-8 border-indigo-200"
        />
      </td>
      <td className="px-2 py-1 text-center">
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={() => removeDraftRow(row.id)}
          className="h-8 w-8 p-0 text-red-500 hover:bg-red-100"
          title="Retirer la ligne"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );

  const renderSavedRow = (inv) => {
    return (
      <tr key={`saved-${inv.id}`} className="border-b bg-white hover:bg-indigo-50/60">
        <td className="px-3 py-2 font-semibold text-gray-800 text-sm">{inv.nom}</td>
        <td className="px-3 py-2 text-gray-700 text-sm">{inv.prenom || '-'}</td>
        <td className="px-3 py-2 text-gray-700 text-sm">
          {inv.email ? (
            <div className="flex items-center gap-1">
              <a href={`mailto:${inv.email}`} className="text-blue-600 hover:underline">{inv.email}</a>
              <span title="Cet email permet la liaison automatique avec le compte investisseur" className="text-green-600 text-xs">✓</span>
            </div>
          ) : (
            <span className="text-amber-600 text-xs italic" title="Sans email, l'investisseur ne verra pas sa mise dans sa session">⚠ manquant</span>
          )}
        </td>
        <td className="px-3 py-2 text-gray-700 text-sm">
          {inv.telephone ? (
            <a href={`tel:${inv.telephone}`} className="text-blue-600 hover:underline">{inv.telephone}</a>
          ) : <span className="text-gray-400 italic">-</span>}
        </td>
        <td className="px-3 py-2 text-gray-600 text-xs font-mono">
          {inv.rib || <span className="text-gray-400 italic">-</span>}
        </td>
        <td className="px-3 py-2 text-right font-bold text-indigo-700 whitespace-nowrap">
          {fmt(inv.mise)} €
        </td>
        <td className="px-3 py-2 text-right text-indigo-700 whitespace-nowrap font-semibold">
          {totalSavedMise > 0 ? `${computePct(inv.mise, totalSavedMise).toFixed(2)} %` : '-'}
        </td>
        <td className="px-3 py-2 whitespace-nowrap text-sm">
          {inv.date_entree ? new Date(inv.date_entree).toLocaleDateString('fr-FR') : '-'}
        </td>
        <td className="px-2 py-2">
          <div className="flex items-center justify-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              type="button"
              className="h-8 w-8 p-0 text-purple-600 hover:bg-purple-100"
              onClick={() => previewInvestorReleve(inv)}
              data-testid={`preview-releve-${inv.id}`}
              title="Aperçu du relevé PDF du mois sélectionné"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              className="h-8 w-8 p-0 text-purple-600 hover:bg-purple-100"
              onClick={() => downloadInvestorReleve(inv)}
              data-testid={`download-releve-${inv.id}`}
              title="Télécharger le relevé PDF du mois sélectionné"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
              onClick={() => setEditing({ ...inv, date_sortie: inv.date_sortie || '' })}
              data-testid={`edit-inv-${inv.id}`}
              title="Modifier"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
              onClick={() => setDeleteTarget(inv)}
              data-testid={`delete-inv-${inv.id}`}
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="investisseurs-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {!hideBackButton && (
            <Button variant="outline" size="sm" onClick={onBack} className="gap-1" data-testid="inv-back-btn">
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
          )}
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-indigo-600" /> Investisseurs
          </h2>
        </div>
      </div>

      {/* Résumé - 2 cartes uniquement (sans Sortis) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-l-4 border-l-green-500 bg-green-50">
          <CardContent className="p-4">
            <p className="text-xs text-green-700 font-semibold uppercase">Investisseurs actifs</p>
            <p className="text-2xl font-bold text-green-800" data-testid="total-actifs">
              {items.length}
            </p>
            <p className="text-xs text-gray-600 mt-1">Total investi : {fmt(items.reduce((s, i) => s + (parseFloat(i.mise) || 0), 0))} €</p>
          </CardContent>
        </Card>
        <Card
          className="border-l-4 border-l-indigo-500 bg-indigo-50 cursor-pointer hover:shadow-lg transition"
          onClick={() => navigate('/repartition-capital')}
          data-testid="capital-total-card"
          title="Voir la répartition du capital (diagramme circulaire)"
        >
          <CardContent className="p-4">
            <p className="text-xs text-indigo-700 font-semibold uppercase flex items-center gap-1">
              <PieChart className="h-3 w-3" /> Capital total investi
            </p>
            <p className="text-2xl font-bold text-indigo-800">
              {fmt(items.reduce((s, i) => s + (parseFloat(i.mise) || 0), 0) + totalDraftMise)} €
            </p>
            <p className="text-xs text-indigo-600 mt-1">→ Voir répartition par investisseur</p>
          </CardContent>
        </Card>
      </div>

      {/* Carte Envoi des relevés mensuels */}
      <Card className="border border-purple-200 bg-purple-50/40" data-testid="send-releves-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="font-semibold text-purple-900 flex items-center gap-2">
                <Mail className="h-4 w-4" /> Relevés mensuels PDF
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Télécharge le relevé d&apos;un investisseur (bouton par ligne), ou envoie le relevé du mois par email à tous les investisseurs via Resend.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                className="border rounded px-2 py-2 text-sm bg-white"
                value={sendMonth}
                onChange={(e) => setSendMonth(parseInt(e.target.value, 10))}
                data-testid="send-month-select"
              >
                {MONTH_LABELS_FR.map((lbl, idx) => (
                  <option key={idx} value={idx + 1}>{lbl}</option>
                ))}
              </select>
              <select
                className="border rounded px-2 py-2 text-sm bg-white"
                value={sendYear}
                onChange={(e) => setSendYear(parseInt(e.target.value, 10))}
                data-testid="send-year-select"
              >
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <Button
                onClick={sendMonthlyReleves}
                disabled={sending || items.length === 0}
                className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="send-releves-btn"
              >
                <Mail className="h-4 w-4" />
                {sending ? 'Envoi...' : 'Envoyer à tous'}
              </Button>
              <Button
                onClick={runScheduler}
                disabled={runningScheduler}
                variant="outline"
                className="gap-2 border-purple-600 text-purple-700 hover:bg-purple-50"
                data-testid="run-scheduler-btn"
                title="Simule l'envoi automatique du 1er du mois"
              >
                <Calendar className="h-4 w-4" />
                {runningScheduler ? 'Exécution…' : 'Envoi auto (mois écoulé)'}
              </Button>
              <Button
                onClick={() => { setShowLogs((v) => !v); if (!logs.length) loadLogs(); }}
                variant="outline"
                className="gap-2"
                data-testid="toggle-logs-btn"
              >
                <FileText className="h-4 w-4" />
                {showLogs ? 'Masquer journal' : 'Journal envois'}
              </Button>
            </div>
          </div>
          {showLogs && (
            <div className="mt-4 border-t pt-4" data-testid="scheduler-logs">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm text-purple-800">Journal des envois automatiques</h4>
                <span className="text-xs text-gray-500">Programmé chaque 1er du mois à 03:00 UTC</span>
              </div>
              {logs.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Aucun envoi automatique enregistré pour le moment.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {logs.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded bg-slate-50 border text-xs">
                      <div>
                        <b className="text-purple-800">{log.period_label}</b>
                        <span className="text-gray-500"> · exécuté le {(log.executed_at || '').slice(0, 16).replace('T', ' ')}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{log.sent_count} envoyé(s)</span>
                        {log.failed_count > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{log.failed_count} échec(s)</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* TABLEAU UNIFIÉ */}
      <Card className="border-2 border-indigo-200">
        <CardHeader className="bg-indigo-50 flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-indigo-700 text-lg">
            Tableau des Investisseurs ({items.length} enregistré{items.length > 1 ? 's' : ''})
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={addDraftRow} className="gap-1" data-testid="add-inv-row-btn">
              <Plus className="h-3 w-3" /> Ligne
            </Button>
            <Button
              size="sm"
              onClick={saveDraft}
              className="bg-indigo-600 hover:bg-indigo-700 gap-1"
              data-testid="save-inv-btn"
            >
              <Save className="h-3 w-3" /> Enregistrer
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-indigo-100 text-indigo-900">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Nom *</th>
                  <th className="px-3 py-2 text-left font-semibold">Prénom</th>
                  <th className="px-3 py-2 text-left font-semibold">Email</th>
                  <th className="px-3 py-2 text-left font-semibold">Téléphone</th>
                  <th className="px-3 py-2 text-left font-semibold">RIB</th>
                  <th className="px-3 py-2 text-right font-semibold">Mise (€) *</th>
                  <th className="px-3 py-2 text-right font-semibold">Part (%)</th>
                  <th className="px-3 py-2 text-left font-semibold">Entrée</th>
                  <th className="px-3 py-2 w-20 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && draft.length === 0 && (
                  <tr>
                    <td colSpan="9" className="text-center text-gray-400 py-6 text-sm">
                      Aucun investisseur enregistré
                    </td>
                  </tr>
                )}

                {/* Lignes à saisir EN PREMIER */}
                {draft.map((row) => renderDraftRow(row))}

                {/* Séparateur */}
                {items.length > 0 && draft.length > 0 && (
                  <tr className="bg-indigo-50/60">
                    <td
                      colSpan="9"
                      className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-indigo-700 border-t border-b border-indigo-200"
                    >
                      Investisseurs déjà enregistrés
                    </td>
                  </tr>
                )}

                {/* Lignes enregistrées */}
                {items.map((inv) => renderSavedRow(inv))}
              </tbody>
              <tfoot className="bg-indigo-100 font-semibold">
                <tr>
                  <td colSpan="5" className="px-3 py-2 text-right text-indigo-900">
                    CAPITAL INVESTI TOTAL :
                  </td>
                  <td className="px-3 py-2 text-right text-indigo-700 text-base">
                    {fmt(items.reduce((s, i) => s + (parseFloat(i.mise) || 0), 0) + totalDraftMise)} €
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={!!previewUrl} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-5xl w-full h-[90vh] flex flex-col p-0" data-testid="preview-releve-modal">
          <DialogHeader className="px-6 py-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-purple-600" />
              {previewMeta
                ? `Aperçu relevé — ${previewMeta.name} — ${MONTH_LABELS_FR[previewMeta.month - 1]} ${previewMeta.year}`
                : 'Aperçu relevé'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-gray-100 overflow-hidden">
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Aperçu du relevé PDF"
                className="w-full h-full border-0"
                data-testid="preview-releve-iframe"
              />
            )}
          </div>
          <div className="flex justify-end gap-2 px-6 py-3 border-t bg-white">
            <Button variant="outline" onClick={closePreview} data-testid="close-preview-admin-btn">
              Fermer
            </Button>
            {previewMeta && (
              <Button
                onClick={() => downloadInvestorReleve(previewMeta.inv)}
                className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Download className="h-4 w-4" /> Télécharger
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-inv-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" /> Modifier l'investisseur
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Nom *</Label>
                  <Input value={editing.nom} onChange={(e) => setEditing((x) => ({ ...x, nom: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm">Prénom</Label>
                  <Input value={editing.prenom || ''} onChange={(e) => setEditing((x) => ({ ...x, prenom: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input type="email" value={editing.email || ''} onChange={(e) => setEditing((x) => ({ ...x, email: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm">Téléphone</Label>
                  <Input value={editing.telephone || ''} onChange={(e) => setEditing((x) => ({ ...x, telephone: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-sm">RIB / IBAN</Label>
                <Input
                  value={editing.rib || ''}
                  onChange={(e) => setEditing((x) => ({ ...x, rib: e.target.value }))}
                  placeholder="FR76 ..."
                  className="font-mono text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Mise *</Label>
                  <Input
                    type="number"
                    value={editing.mise}
                    onChange={(e) => setEditing((x) => ({ ...x, mise: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    step="0.01"
                    data-testid="edit-mise"
                  />
                </div>
                <div>
                  <Label className="text-sm">Devise</Label>
                  <Select
                    value={editing.devise || 'EUR'}
                    onValueChange={(v) => setEditing((x) => ({ ...x, devise: v }))}
                  >
                    <SelectTrigger data-testid="edit-devise"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="CNY">CNY (¥)</SelectItem>
                      <SelectItem value="XAF">FCFA (XAF)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Part (%)</Label>
                  <div
                    className="h-10 flex items-center justify-end px-3 text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md"
                    title="Calculé automatiquement à partir de la mise / capital total"
                  >
                    {totalSavedMise > 0 ? `${computePct(editing.mise, totalSavedMise).toFixed(2)} %` : '-'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Date d'entrée</Label>
                  <Input type="date" value={editing.date_entree} onChange={(e) => setEditing((x) => ({ ...x, date_entree: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm">Statut</Label>
                  <Select value={editing.statut} onValueChange={(v) => setEditing((x) => ({ ...x, statut: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actif">Actif</SelectItem>
                      <SelectItem value="sorti">Sorti</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Date de sortie</Label>
                  <Input
                    type="date"
                    value={editing.date_sortie || ''}
                    onChange={(e) => setEditing((x) => ({ ...x, date_sortie: e.target.value }))}
                    disabled={editing.statut !== 'sorti'}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-row">
            <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Annuler</Button>
            <Button
              onClick={submitEdit}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-edit-inv-btn"
            >
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer cet investisseur ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            Confirmer la suppression de <strong>{deleteTarget?.nom} {deleteTarget?.prenom || ''}</strong> (
            mise {fmt(deleteTarget?.mise || 0)} €) ?
          </p>
          <DialogFooter className="gap-2 flex-row">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1">Annuler</Button>
            <Button
              onClick={confirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700"
              data-testid="inv-confirm-delete-btn"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
